import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { BOHOL_MUNICIPALITIES } from '../data/boholLocations';
import { Plus, Pencil, Trash2, ExternalLink, Loader2, ArrowLeft, Users, ChevronDown, ChevronUp, Mail, Check, Settings, RotateCcw, Send, Eye, EyeOff, Sparkles, Search, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import RichTextEditor from '../components/RichTextEditor';
import ImageUpload from '../components/ImageUpload';

const EMPTY_FORM = {
  title: '',
  description: '',
  category_id: '',
  locations: [],
  salary_min: '',
  salary_max: '',
  salary_period: 'daily',
  work_schedule: 'full_time',
  contact_email: '',
  original_url: '',
  requirements: '',
  image_url: null,
  image_position: 50,
  urgent_hiring: false,
  no_experience_needed: false,
  positions_available: 1
};

const CommunityBoardAdmin = () => {
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [expandedJob, setExpandedJob] = useState(null);
  const [applicants, setApplicants] = useState({});
  const [applicantsLoading, setApplicantsLoading] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [sendingOutreach, setSendingOutreach] = useState(null);

  // Template editor states
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [templateData, setTemplateData] = useState({ subject: '', body: '' });
  const [defaultTemplate, setDefaultTemplate] = useState({ subject: '', body: '' });
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Search & Pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const JOBS_PER_PAGE = 20;

  // AI Improve
  const [aiImproving, setAiImproving] = useState(false);

  const getToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
  };

  const fetchJobs = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) return;
      const res = await api.get('/api/admin/community-board/jobs', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(res.data.jobs || []);
    } catch (err) {
      console.error('Failed to fetch CB jobs:', err);
      toast.error('Failed to load community board jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/api/categories');
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchCategories();
  }, [fetchJobs]);

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM });
    setEditingId(null);
    setShowForm(false);
  };

  const openCreateForm = () => {
    setFormData({ ...EMPTY_FORM });
    setEditingId(null);
    setShowForm(true);
  };

  const openEditForm = (job) => {
    setFormData({
      title: job.title || '',
      description: job.description || '',
      category_id: job.category_id || '',
      locations: job.locations || (job.location ? [job.location] : []),
      salary_min: job.salary_min || '',
      salary_max: job.salary_max || '',
      salary_period: job.salary_period || 'daily',
      work_schedule: job.work_schedule || 'full_time',
      contact_email: job.contact_email || '',
      original_url: job.original_url || '',
      requirements: (job.requirements || []).join('\n'),
      image_url: job.image_url || null,
      image_position: job.image_position || 50,
      urgent_hiring: job.urgent_hiring || false,
      no_experience_needed: job.no_experience_needed || false,
      positions_available: job.positions_available || 1
    });
    setEditingId(job.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title?.trim() || formData.title.trim().length < 3) {
      toast.error('Title must be at least 3 characters');
      return;
    }
    if (!formData.description?.trim() || formData.description.trim().length < 20) {
      toast.error('Description must be at least 20 characters');
      return;
    }
    if (!formData.category_id) {
      toast.error('Please select a category');
      return;
    }
    if (!formData.locations || formData.locations.length === 0) {
      toast.error('Please select at least one location');
      return;
    }
    if (!formData.contact_email?.trim()) {
      toast.error('Contact email is required');
      return;
    }

    // Validate salary if provided
    if (formData.salary_min || formData.salary_max) {
      const min = Number(formData.salary_min);
      const max = Number(formData.salary_max);
      if (formData.salary_min && (isNaN(min) || min <= 0)) {
        toast.error('Salary min must be a positive number');
        return;
      }
      if (formData.salary_max && (isNaN(max) || max <= 0)) {
        toast.error('Salary max must be a positive number');
        return;
      }
      if (formData.salary_min && formData.salary_max && min > max) {
        toast.error('Salary min cannot be greater than max');
        return;
      }
    }

    setSubmitting(true);
    try {
      const token = await getToken();
      if (!token) {
        toast.error('Not authenticated');
        return;
      }

      const requirements = (formData.requirements || '')
        .split('\n')
        .filter(r => r.trim())
        .map(r => r.trim());

      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category_id: formData.category_id,
        locations: formData.locations,
        contact_email: formData.contact_email.trim(),
        work_schedule: formData.work_schedule,
        requirements,
        image_url: formData.image_url || null,
        image_position: formData.image_position,
        urgent_hiring: formData.urgent_hiring,
        no_experience_needed: formData.no_experience_needed,
        positions_available: formData.positions_available || 1
      };

      if (formData.salary_min) payload.salary_min = Math.floor(Number(formData.salary_min));
      if (formData.salary_max) payload.salary_max = Math.floor(Number(formData.salary_max));
      if (formData.salary_min || formData.salary_max) payload.salary_period = formData.salary_period;
      if (formData.original_url?.trim()) payload.original_url = formData.original_url.trim();

      const headers = { Authorization: `Bearer ${token}` };

      if (editingId) {
        await api.put(`/api/admin/community-board/jobs/${editingId}`, payload, { headers });
        toast.success('Job updated successfully');
      } else {
        await api.post('/api/admin/community-board/jobs', payload, { headers });
        toast.success('Job created successfully');
      }

      resetForm();
      fetchJobs();
    } catch (err) {
      console.error('Submit error:', err);
      toast.error(err.response?.data?.detail || 'Failed to save job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (jobId) => {
    try {
      const token = await getToken();
      if (!token) return;
      await api.delete(`/api/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Job deleted');
      setDeleteConfirm(null);
      fetchJobs();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete job');
    }
  };

  const fetchApplicants = async (jobId) => {
    if (expandedJob === jobId) {
      setExpandedJob(null);
      return;
    }
    setApplicantsLoading(jobId);
    try {
      const token = await getToken();
      if (!token) return;
      const res = await api.get(`/api/applications/job/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplicants(prev => ({ ...prev, [jobId]: res.data.applications || [] }));
      setExpandedJob(jobId);
    } catch (err) {
      console.error('Failed to fetch applicants:', err);
      toast.error('Failed to load applicants');
    } finally {
      setApplicantsLoading(null);
    }
  };

  const handleSendOutreach = async (jobId) => {
    setSendingOutreach(jobId);
    try {
      const token = await getToken();
      if (!token) return;
      await api.post(`/api/admin/community-board/jobs/${jobId}/send-outreach`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Outreach email sent successfully');
      fetchJobs(); // Refresh to show outreach_email_sent_at
    } catch (err) {
      console.error('Outreach error:', err);
      toast.error(err.response?.data?.detail || 'Failed to send outreach email');
    } finally {
      setSendingOutreach(null);
    }
  };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat?.name || '-';
  };

  // Filter jobs by search term — email has highest priority (exact matches first)
  const filteredJobs = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return jobs;

    // Score each job: email match = 3, title match = 2, location/category = 1
    const scored = jobs
      .map(job => {
        let score = 0;
        if ((job.contact_email || '').toLowerCase().includes(q)) score += 3;
        if ((job.title || '').toLowerCase().includes(q)) score += 2;
        const locStr = (job.locations || []).join(' ').toLowerCase();
        if (locStr.includes(q) || (job.location || '').toLowerCase().includes(q)) score += 1;
        if (getCategoryName(job.category_id).toLowerCase().includes(q)) score += 1;
        return { job, score };
      })
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score);

    return scored.map(({ job }) => job);
  }, [jobs, searchTerm, categories]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const fetchTemplate = async () => {
    setTemplateLoading(true);
    try {
      const token = await getToken();
      if (!token) return;
      const res = await api.get('/api/admin/community-board/outreach-template', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplateData(res.data.template || { subject: '', body: '' });
      setDefaultTemplate(res.data.default_template || { subject: '', body: '' });
    } catch (err) {
      console.error('Failed to fetch template:', err);
      toast.error('Failed to load template');
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateData.subject?.trim() || !templateData.body?.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    setTemplateSaving(true);
    try {
      const token = await getToken();
      if (!token) return;
      await api.put('/api/admin/community-board/outreach-template', {
        subject: templateData.subject,
        body: templateData.body
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template saved');
    } catch (err) {
      console.error('Failed to save template:', err);
      toast.error(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleResetTemplate = () => {
    setTemplateData({ ...defaultTemplate });
    toast.info('Template reset to default (not saved yet)');
  };

  const handleAIImprove = async () => {
    if (!formData.title?.trim() || formData.title.trim().length < 3) {
      toast.error('Enter a job title first (at least 3 characters)');
      return;
    }
    setAiImproving(true);
    try {
      const token = await getToken();
      if (!token) return;

      const categoryName = getCategoryName(formData.category_id) || 'General';
      const response = await api.post('/api/ai/generate-job-post', {
        job_title: formData.title.trim(),
        category: categoryName,
        location: formData.locations?.[0] || 'Bohol',
        employment_type: formData.work_schedule || 'full_time',
        experience_level: 'any',
        education_level: 'any',
        skills: [],
        salary_min: formData.salary_min ? Number(formData.salary_min) : null,
        salary_max: formData.salary_max ? Number(formData.salary_max) : null,
        salary_period: formData.salary_period || 'monthly',
        work_schedule: formData.work_schedule || 'full_time',
        benefits: [],
        description_length: 'concise'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const result = response.data?.data || response.data;
      // Merge AI requirements with existing ones (don't replace)
      let mergedRequirements = prev => {
        const existing = (prev.requirements || '').split('\n').map(r => r.trim()).filter(Boolean);
        const aiReqs = Array.isArray(result.requirements)
          ? result.requirements.map(r => r.trim()).filter(Boolean)
          : [];
        // Add only AI requirements that aren't already present
        const newReqs = aiReqs.filter(r => !existing.some(e => e.toLowerCase() === r.toLowerCase()));
        return existing.length > 0
          ? [...existing, ...newReqs].join('\n')
          : aiReqs.join('\n');
      };
      setFormData(prev => ({
        ...prev,
        title: result.generated_title || prev.title,
        description: result.full_description || result.description || prev.description,
        requirements: mergedRequirements(prev),
      }));
      toast.success('AI improved title and description. Review before saving.');
    } catch (err) {
      console.error('AI improve error:', err);
      toast.error(err.response?.data?.detail || 'AI generation failed');
    } finally {
      setAiImproving(false);
    }
  };

  const getPreviewHtml = () => {
    return (templateData.body || '')
      .replace(/\{employer_name\}/g, 'Juan Dela Cruz')
      .replace(/\{job_title\}/g, 'Cashier at SM Tagbilaran')
      .replace(/\{claim_url\}/g, '#')
      .replace(/\{platform_url\}/g, 'https://www.boholtrabaho.com')
      .replace(/\{job_url\}/g, '#');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-heading font-bold">Community Board</h2>
          <p className="text-sm text-muted-foreground">
            Curate job posts from Facebook groups for the platform
          </p>
        </div>
        {!showForm && (
          <Button onClick={openCreateForm}>
            <Plus size={16} className="mr-2" />
            Add Job
          </Button>
        )}
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{editingId ? 'Edit Community Job' : 'Add Community Job'}</CardTitle>
                <CardDescription>
                  {editingId ? 'Update the job listing details' : 'Create a new job listing from a Facebook post'}
                </CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={resetForm}>
                <ArrowLeft size={16} className="mr-1" />
                Cancel
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Community Board Fields */}
              <div className="p-4 border-2 border-cyan-200 rounded-xl bg-cyan-50/50 space-y-4">
                <h3 className="text-sm font-semibold text-cyan-800">Community Board Info</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Contact Email *</Label>
                    <Input
                      type="email"
                      placeholder="employer@email.com"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      className="rounded-xl bg-white"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      From the Facebook post - used for apply-via-email and outreach
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Original Facebook URL</Label>
                    <Input
                      type="url"
                      placeholder="https://facebook.com/..."
                      value={formData.original_url}
                      onChange={(e) => setFormData({ ...formData, original_url: e.target.value })}
                      className="rounded-xl bg-white"
                    />
                    <p className="text-xs text-muted-foreground">
                      Admin-only reference (never shown to seekers)
                    </p>
                  </div>
                </div>
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label>Job Title *</Label>
                <Input
                  placeholder="e.g., Cashier Needed at SM Tagbilaran"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="rounded-xl"
                  required
                />
              </div>

              {/* Category & Work Schedule */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                    required
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Work Schedule</Label>
                  <Select
                    value={formData.work_schedule}
                    onValueChange={(value) => setFormData({ ...formData, work_schedule: value })}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Full Time</SelectItem>
                      <SelectItem value="part_time">Part Time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Location (Multi-select) */}
              <div className="space-y-2">
                <Label>Location(s) *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="flex w-full items-center justify-between rounded-xl border border-input bg-white px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 min-h-[40px]">
                      <span className={formData.locations.length === 0 ? 'text-muted-foreground' : ''}>
                        {formData.locations.length === 0 ? 'Select municipalities' : `${formData.locations.length} selected`}
                      </span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-50"><path d="m6 9 6 6 6-6"/></svg>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <div className="max-h-[250px] overflow-y-auto p-2" onWheel={(e) => e.stopPropagation()}>
                      {BOHOL_MUNICIPALITIES.map((m) => (
                        <label key={m} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm">
                          <Checkbox
                            checked={formData.locations.includes(m)}
                            onCheckedChange={(checked) => {
                              const next = checked
                                ? [...formData.locations, m]
                                : formData.locations.filter(l => l !== m);
                              setFormData({ ...formData, locations: next });
                            }}
                          />
                          {m}
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                {formData.locations.length > 0 && (
                  <p className="text-xs text-muted-foreground">{formData.locations.join(', ')}</p>
                )}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Description *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 px-3 text-xs border-pink-200 text-pink-700 hover:bg-pink-50"
                    onClick={handleAIImprove}
                    disabled={aiImproving || !formData.title?.trim()}
                    title="AI will improve the title, description, and generate requirements based on the job title"
                  >
                    {aiImproving ? (
                      <><Loader2 size={12} className="mr-1 animate-spin" />Generating...</>
                    ) : (
                      <><Sparkles size={12} className="mr-1" />AI Improve</>
                    )}
                  </Button>
                </div>
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => setFormData({ ...formData, description: value })}
                  placeholder="Rewrite or clean up the Facebook post content..."
                  className="rounded-xl"
                />
                {!formData.description && (
                  <p className="text-xs text-muted-foreground">
                    Tip: Enter a job title first, then click "AI Improve" to auto-generate a professional description
                  </p>
                )}
              </div>

              {/* Salary (Optional) */}
              <div className="space-y-2">
                <Label>Salary Range (Optional)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Leave empty if the Facebook post doesn't mention salary
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Input
                      type="number"
                      placeholder="Min"
                      value={formData.salary_min}
                      onChange={(e) => setFormData({ ...formData, salary_min: e.target.value })}
                      onWheel={(e) => e.target.blur()}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-1">
                    <Input
                      type="number"
                      placeholder="Max"
                      value={formData.salary_max}
                      onChange={(e) => setFormData({ ...formData, salary_max: e.target.value })}
                      onWheel={(e) => e.target.blur()}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-1">
                    <Select
                      value={formData.salary_period}
                      onValueChange={(value) => setFormData({ ...formData, salary_period: value })}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Per Day</SelectItem>
                        <SelectItem value="monthly">Per Month</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Requirements */}
              <div className="space-y-2">
                <Label>Requirements (one per line)</Label>
                <Textarea
                  placeholder="Experience with customer service&#10;High school graduate"
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  rows={3}
                  className="rounded-xl"
                />
              </div>

              {/* Positions */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Number of Positions</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.positions_available}
                    onChange={(e) => setFormData({ ...formData, positions_available: Math.max(1, parseInt(e.target.value) || 1) })}
                    onWheel={(e) => e.target.blur()}
                    className="rounded-xl"
                  />
                </div>
              </div>

              {/* Image Upload */}
              <div className="space-y-2">
                <ImageUpload
                  onImageUpload={(imageUrl) => setFormData(prev => ({ ...prev, image_url: imageUrl }))}
                  currentImage={formData.image_url}
                  label="Job Image (Optional)"
                  imagePosition={formData.image_position}
                  onPositionChange={(pos) => setFormData(prev => ({ ...prev, image_position: pos }))}
                />
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={resetForm} className="rounded-xl">
                  Cancel
                </Button>
                <Button type="submit" className="rounded-xl flex-1" disabled={submitting}>
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                  ) : editingId ? (
                    'Update Job'
                  ) : (
                    <><Plus size={16} className="mr-2" />Create Job</>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Jobs Listing Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-lg">Community Board Jobs ({jobs.length})</CardTitle>
            <div className="relative w-full sm:w-64">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search email, title, location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-8 h-9 rounded-xl text-sm"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
          {searchTerm && filteredJobs.length !== jobs.length && (
            <p className="text-xs text-muted-foreground mt-2">
              {filteredJobs.length} result{filteredJobs.length !== 1 ? 's' : ''} for "{searchTerm}"
            </p>
          )}
        </CardHeader>
        <CardContent>
          {filteredJobs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users size={40} className="mx-auto mb-3 opacity-50" />
              <p>{searchTerm ? 'No jobs match your search' : 'No community board jobs yet'}</p>
              <p className="text-sm mt-1">{searchTerm ? 'Try a different search term' : 'Click "Add Job" to curate one from Facebook'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead className="hidden md:table-cell">Location</TableHead>
                    <TableHead className="hidden md:table-cell">Category</TableHead>
                    <TableHead className="hidden md:table-cell">Contact</TableHead>
                    <TableHead className="text-center">Applied</TableHead>
                    <TableHead className="hidden md:table-cell">Status</TableHead>
                    <TableHead className="hidden md:table-cell text-center">Outreach</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredJobs.slice((page - 1) * JOBS_PER_PAGE, page * JOBS_PER_PAGE).map(job => (
                    <React.Fragment key={job.id}>
                      <TableRow className="group">
                        <TableCell>
                          <div className="min-w-[150px]">
                            <p className="font-medium text-sm truncate max-w-[200px]">{job.title}</p>
                            <p className="text-xs text-muted-foreground md:hidden">
                              {(job.locations?.length > 0 ? job.locations.join(', ') : job.location) || '-'}
                            </p>
                            {job.is_claimed && (
                              <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Claimed</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm">
                          {(job.locations?.length > 0 ? job.locations.join(', ') : job.location) || '-'}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm">
                          {getCategoryName(job.category_id)}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <span className="text-xs text-muted-foreground truncate max-w-[150px] block">
                            {job.contact_email || '-'}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-sm font-medium"
                            onClick={() => fetchApplicants(job.id)}
                            disabled={applicantsLoading === job.id}
                          >
                            {applicantsLoading === job.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                {job.application_count || 0}
                                {expandedJob === job.id ? (
                                  <ChevronUp size={14} className="ml-1" />
                                ) : (
                                  <ChevronDown size={14} className="ml-1" />
                                )}
                              </>
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            job.status === 'open' ? 'bg-green-100 text-green-700' :
                            job.status === 'closed' ? 'bg-gray-100 text-gray-600' :
                            job.status === 'expired' ? 'bg-red-100 text-red-600' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {job.status}
                          </span>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-center">
                          {job.is_claimed ? (
                            <span className="text-xs text-green-600">Claimed</span>
                          ) : job.outreach_email_sent_at ? (
                            <span className="text-xs text-muted-foreground" title={`Sent ${new Date(job.outreach_email_sent_at).toLocaleString()}`}>
                              <Check size={14} className="inline mr-1 text-green-600" />
                              Sent
                            </span>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700"
                              onClick={() => handleSendOutreach(job.id)}
                              disabled={sendingOutreach === job.id}
                              title="Send outreach email to employer"
                            >
                              {sendingOutreach === job.id ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <><Mail size={12} className="mr-1" />Send</>
                              )}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {job.original_url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => window.open(job.original_url, '_blank')}
                                title="Open original FB post"
                              >
                                <ExternalLink size={14} />
                              </Button>
                            )}
                            {!job.is_claimed && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => openEditForm(job)}
                                title="Edit"
                              >
                                <Pencil size={14} />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                              onClick={() => setDeleteConfirm(job.id)}
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>

                      {/* Expanded applicants row */}
                      {expandedJob === job.id && applicants[job.id] && (
                        <TableRow>
                          <TableCell colSpan={8} className="bg-gray-50 p-4">
                            {applicants[job.id].length === 0 ? (
                              <p className="text-sm text-muted-foreground text-center py-2">
                                No applications yet
                              </p>
                            ) : (
                              <div className="space-y-2">
                                <p className="text-xs font-medium text-muted-foreground uppercase">
                                  Applicants ({applicants[job.id].length})
                                </p>
                                <div className="divide-y">
                                  {applicants[job.id].map(app => (
                                    <div key={app.id} className="py-2 flex items-center justify-between text-sm">
                                      <div>
                                        <span className="font-medium">
                                          {app.seeker?.name || app.applicant?.name || 'Unknown'}
                                        </span>
                                        <span className="text-muted-foreground ml-2">
                                          {app.seeker?.email || app.applicant?.email || ''}
                                        </span>
                                      </div>
                                      <span className="text-xs text-muted-foreground">
                                        {new Date(app.created_at).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric'
                                        })}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {filteredJobs.length > JOBS_PER_PAGE && (
            <div className="flex items-center justify-between pt-4 border-t mt-4">
              <p className="text-sm text-muted-foreground">
                Showing {(page - 1) * JOBS_PER_PAGE + 1}-{Math.min(page * JOBS_PER_PAGE, filteredJobs.length)} of {filteredJobs.length}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(Math.ceil(filteredJobs.length / JOBS_PER_PAGE), p + 1))}
                  disabled={page >= Math.ceil(filteredJobs.length / JOBS_PER_PAGE)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tools Section: Outreach email template */}
      <div className="grid gap-4">
        {/* Outreach Email Template */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Settings size={16} />
                Outreach Email Template
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (!showTemplateEditor) {
                    fetchTemplate();
                  }
                  setShowTemplateEditor(!showTemplateEditor);
                }}
              >
                {showTemplateEditor ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </Button>
            </div>
          </CardHeader>
          {showTemplateEditor && (
            <CardContent className="pt-0 space-y-4">
              {templateLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {/* Placeholders */}
                  <div className="flex flex-wrap gap-1.5">
                    {['{employer_name}', '{job_title}', '{claim_url}', '{platform_url}', '{job_url}'].map(p => (
                      <button
                        key={p}
                        type="button"
                        className="text-xs bg-cyan-100 text-cyan-700 px-2 py-1 rounded-full hover:bg-cyan-200 transition-colors"
                        onClick={() => navigator.clipboard?.writeText(p).then(() => toast.info(`Copied ${p}`))}
                        title="Click to copy"
                      >
                        {p}
                      </button>
                    ))}
                  </div>

                  {/* Subject */}
                  <div className="space-y-1.5">
                    <Label className="text-sm">Subject Line</Label>
                    <Input
                      value={templateData.subject}
                      onChange={(e) => setTemplateData({ ...templateData, subject: e.target.value })}
                      placeholder="Email subject..."
                      className="rounded-xl text-sm"
                    />
                  </div>

                  {/* Body */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Body (HTML)</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => setShowPreview(!showPreview)}
                      >
                        {showPreview ? <><EyeOff size={12} className="mr-1" />Edit</> : <><Eye size={12} className="mr-1" />Preview</>}
                      </Button>
                    </div>
                    {showPreview ? (
                      <div
                        className="border rounded-xl p-4 text-sm bg-white min-h-[200px] max-h-[400px] overflow-auto"
                        dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
                      />
                    ) : (
                      <Textarea
                        value={templateData.body}
                        onChange={(e) => setTemplateData({ ...templateData, body: e.target.value })}
                        placeholder="Email body HTML..."
                        rows={10}
                        className="rounded-xl text-xs font-mono"
                      />
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl"
                      onClick={handleResetTemplate}
                    >
                      <RotateCcw size={14} className="mr-1" />
                      Reset to Default
                    </Button>
                    <Button
                      size="sm"
                      className="rounded-xl flex-1"
                      onClick={handleSaveTemplate}
                      disabled={templateSaving}
                    >
                      {templateSaving ? (
                        <><Loader2 size={14} className="mr-1 animate-spin" />Saving...</>
                      ) : (
                        'Save Template'
                      )}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          )}
        </Card>

      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Community Board Job?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will permanently remove this job listing. This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)}>
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommunityBoardAdmin;
